import { ShieldCheckIcon, ClockIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { styled } from "styled-components";
import { HtmlContent } from "../components";
import Row from "../containers/Row";
import { StyledButton, StyledTitle } from "../components/Typography";
import { formatPrice } from "../helpers/textTransformation";
import Stepper from "../containers/Stepper";
import { useCart } from "../contexts";
import { useNavigate } from "react-router-dom";
import { createAuctionPlaceholderImage } from "../lib/auctionPresentation";

const PageShell = styled.div`
  width: 100%;
  background:
    radial-gradient(circle at top right, rgba(246, 163, 28, 0.14), transparent 28%),
    linear-gradient(180deg, #f9fafc 0%, #ffffff 24%);
`;

const PageContent = styled.div`
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  padding: 32px 0 72px;
  display: flex;
  flex-direction: column;
  gap: 28px;

  @media (max-width: 767px) {
    padding: 20px 0 44px;
    gap: 20px;
  }
`;

const HeroCard = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(280px, 420px);
  gap: 24px;
  padding: 28px 32px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top right, rgba(246, 163, 28, 0.2), transparent 26%),
    linear-gradient(135deg, #0c1129 0%, #11193d 100%);
  color: ${({ theme }) => theme.colors.white};
  overflow: hidden;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
    padding: 24px;
  }

  @media (max-width: 767px) {
    padding: 22px 18px;
    border-radius: 24px;
  }
`;

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const HeroEyebrow = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-family: "Sofia Pro", sans-serif;
  font-size: 42px;
  line-height: 1.05;
  font-weight: 700;

  @media (max-width: 767px) {
    font-size: 32px;
  }
`;

const HeroText = styled.p`
  margin: 0;
  max-width: 680px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 17px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.84);

  @media (max-width: 767px) {
    font-size: 15px;
    line-height: 1.6;
  }
`;

const TrustGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 8px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const TrustCard = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 16px 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const TrustIconWrap = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(246, 163, 28, 0.18);
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
`;

const TrustTitle = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 3px;
`;

const TrustText = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.74);
`;

const HeroProductCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 18px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
`;

const HeroProductImage = styled.img`
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
`;

const HeroProductMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HeroProductLabel = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
`;

const HeroProductTitle = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 28px;
  line-height: 1.15;
  font-weight: 700;
`;

const HeroPrice = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 4px;
`;

const HeroPriceValue = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 34px;
  line-height: 1;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const HeroPriceCaption = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.68);
`;

const CheckoutGrid = styled.div<{ $fullWidth?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $fullWidth }) =>
    $fullWidth ? "minmax(0, 1fr)" : "minmax(0, 1.7fr) minmax(300px, 360px)"};
  gap: 24px;
  align-items: start;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.div`
  min-width: 0;
`;

const SummaryColumn = styled.aside`
  position: sticky;
  top: 108px;

  @media (max-width: 1023px) {
    position: static;
  }
`;

const SummaryCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  border-radius: 24px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 18px 48px rgba(13, 18, 45, 0.08);
  border: 1px solid rgba(10, 17, 41, 0.06);
`;

const SummaryImage = styled.img`
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: 18px;
  background: ${({ theme }) => theme.colors.grey100};
`;

const SummaryHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SummaryOverline = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
`;

const SummaryTitle = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 26px;
  line-height: 1.15;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};
`;

const StatusBadge = styled.div`
  width: fit-content;
  padding: 8px 12px;
  border-radius: 999px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue500};
  background: ${({ theme }) => `${theme.colors.primary}22`};
`;

const SummaryRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  font-family: "Sofia Pro", sans-serif;
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.blue900};
`;

const SummaryLabel = styled.span`
  color: ${({ theme }) => theme.colors.grey};
`;

const SummaryValue = styled.span`
  font-weight: 600;
  text-align: right;
`;

const SummaryDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
`;

const SummaryDescription = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.blue900};

  ul {
    margin: 0;
    padding-left: 18px;
  }

  li + li {
    margin-top: 6px;
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const TotalBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
`;

const TotalLabel = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${({ theme }) => theme.colors.grey};
`;

const TotalValue = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 32px;
  line-height: 1;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  text-align: right;
`;

const SummaryHint = styled.div`
  padding: 14px 16px;
  border-radius: 18px;
  background: ${({ theme }) => theme.colors.grey100};
  font-family: "Sofia Pro", sans-serif;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.blue500};

  @media (max-width: 767px) {
    display: none;
  }
`;

const EmptyStateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  padding: 48px 28px;
  border-radius: 28px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 18px 48px rgba(13, 18, 45, 0.08);
  text-align: center;
`;

const EmptyText = styled.p`
  margin: 0;
  max-width: 520px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 16px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.blue500};
`;

const EmptyButton = styled(StyledButton)`
  padding: 14px 28px;
`;

const STEP_STATUS_LABELS = [
  "Kontrola položky",
  "Vyplnění údajů",
  "Dokončení platby",
  "Objednávka potvrzena",
];

const getStepCopy = (step: number) => {
  if (step === 1) {
    return {
      eyebrow: "Dodací údaje",
      title: "Ještě kousek a je hotovo",
      description:
        "Vyplňte kontaktní a fakturační údaje. Formulář jsme zjednodušili tak, aby vás nezdržoval.",
    };
  }

  if (step === 2) {
    return {
      eyebrow: "Platba",
      title: "Potvrďte objednávku bez zbytečného chaosu",
      description:
        "Na jednom místě vidíte souhrn objednávky, zbývající čas i stav platební brány. Žádné slepé kroky navíc.",
    };
  }

  if (step === 3) {
    return {
      eyebrow: "Souhrn",
      title: "Objednávka je uzavřena",
      description:
        "Finální potvrzení má vlastní klidný prostor bez duplicitního sidebaru. Všechno důležité je na jednom místě.",
    };
  }

  return {
    eyebrow: "Košík",
    title: "Dokončení objednávky",
    description:
      "Zkontrolujte výherní položku, doplňte údaje a projděte platbou v čistém čtyřkrokovém flow, které funguje na desktopu i mobilu.",
  };
};

const ShoppingCart = () => {
  const { cartProduct: product, checkoutStep } = useCart();
  const navigate = useNavigate();

  if (!product) {
    return (
      <PageShell>
        <Row>
          <PageContent>
            <HeroCard>
              <HeroCopy>
                <HeroEyebrow>Košík</HeroEyebrow>
                <HeroTitle>Košík je momentálně prázdný</HeroTitle>
                <HeroText>
                  Vyberte si aktuální aukci, přidejte ji do košíku a dokončete
                  objednávku v přehledném checkoutu bez zbytečného přepínání mezi stránkami.
                </HeroText>
              </HeroCopy>
            </HeroCard>

            <EmptyStateCard>
              <StyledTitle>Nemáte vybranou žádnou položku</StyledTitle>
              <EmptyText>
                Jakmile zvolíte výhru nebo aktivní aukci, připravíme vám zde
                celý objednávkový proces včetně shrnutí a platebního kroku.
              </EmptyText>
              <EmptyButton onClick={() => navigate("/")}>
                Zpět na aukce
              </EmptyButton>
            </EmptyStateCard>
          </PageContent>
        </Row>
      </PageShell>
    );
  }

  const heroCopy = getStepCopy(checkoutStep);
  const summaryImageFallback = createAuctionPlaceholderImage(product.title);
  const showSummarySidebar = checkoutStep !== 3;

  return (
    <PageShell>
      <Row>
        <PageContent>
          <HeroCard>
            <HeroCopy>
              <HeroEyebrow>{heroCopy.eyebrow}</HeroEyebrow>
              <HeroTitle>{heroCopy.title}</HeroTitle>
              <HeroText>{heroCopy.description}</HeroText>
              <TrustGrid>
                <TrustCard>
                  <TrustIconWrap>
                    <ShieldCheckIcon style={{ width: 20, height: 20 }} />
                  </TrustIconWrap>
                  <div>
                    <TrustTitle>Bezpečný checkout</TrustTitle>
                    <TrustText>
                      Jasně oddělené kroky, čitelné formuláře a stabilní shrnutí objednávky.
                    </TrustText>
                  </div>
                </TrustCard>
                <TrustCard>
                  <TrustIconWrap>
                    <ClockIcon style={{ width: 20, height: 20 }} />
                  </TrustIconWrap>
                  <div>
                    <TrustTitle>Rychlý průchod</TrustTitle>
                    <TrustText>
                      Většina akcí je na jedné obrazovce, takže se v checkoutu neztratíte.
                    </TrustText>
                  </div>
                </TrustCard>
                <TrustCard>
                  <TrustIconWrap>
                    <SparklesIcon style={{ width: 20, height: 20 }} />
                  </TrustIconWrap>
                  <div>
                    <TrustTitle>Lepší mobilní UX</TrustTitle>
                    <TrustText>
                      Obsah se skládá do přirozeného pořadí a CTA tlačítka zůstávají čitelná.
                    </TrustText>
                  </div>
                </TrustCard>
              </TrustGrid>
            </HeroCopy>

            <HeroProductCard>
              <HeroProductImage
                src={product.photoUrls?.[0] || summaryImageFallback}
                alt={product.title}
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = summaryImageFallback;
                }}
              />
              <HeroProductMeta>
                <HeroProductLabel>Vybraná položka</HeroProductLabel>
                <HeroProductTitle>{product.title}</HeroProductTitle>
                <HeroPrice>
                  <HeroPriceValue>{formatPrice(product.price)}</HeroPriceValue>
                  <HeroPriceCaption>aktuální cena objednávky</HeroPriceCaption>
                </HeroPrice>
              </HeroProductMeta>
            </HeroProductCard>
          </HeroCard>

          <CheckoutGrid $fullWidth={!showSummarySidebar}>
            <MainColumn>
              <Stepper />
            </MainColumn>

            {showSummarySidebar && (
              <SummaryColumn>
                <SummaryCard>
                  <SummaryImage
                    src={product.photoUrls?.[0] || summaryImageFallback}
                    alt={product.title}
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = summaryImageFallback;
                    }}
                  />

                  <SummaryHeader>
                    <SummaryOverline>Shrnutí objednávky</SummaryOverline>
                    <SummaryTitle>{product.title}</SummaryTitle>
                    <StatusBadge>{STEP_STATUS_LABELS[checkoutStep] || STEP_STATUS_LABELS[0]}</StatusBadge>
                  </SummaryHeader>

                  <SummaryRows>
                    <SummaryRow>
                      <SummaryLabel>Produkt</SummaryLabel>
                      <SummaryValue>{product.title}</SummaryValue>
                    </SummaryRow>
                    {product.providerDescription && (
                      <SummaryRow>
                        <SummaryLabel>Partner</SummaryLabel>
                        <SummaryValue>{product.providerDescription}</SummaryValue>
                      </SummaryRow>
                    )}
                  </SummaryRows>

                  {product.description && (
                    <>
                      <SummaryDivider />
                      <SummaryDescription>
                        <HtmlContent html={product.description} />
                      </SummaryDescription>
                    </>
                  )}

                  <SummaryDivider />

                  <TotalBlock>
                    <div>
                      <TotalLabel>Celkem</TotalLabel>
                    </div>
                    <TotalValue>{formatPrice(product.price)}</TotalValue>
                  </TotalBlock>

                  <SummaryHint>
                    Souhrn zůstává po ruce i během vyplňování údajů a platebního kroku,
                    takže vždy vidíte, co přesně dokončujete.
                  </SummaryHint>
                </SummaryCard>
              </SummaryColumn>
            )}
          </CheckoutGrid>
        </PageContent>
      </Row>
    </PageShell>
  );
};

export default ShoppingCart;
